import _ from "lodash";

import { AnyUser } from "@megaron/access-control";
import { HttpAction } from "@megaron/http-service";
import { HandlerMap } from "@megaron/http-service-host";
import { Serializer, Serializers } from "@megaron/serializers";

import { DiffSyncBaseSchema, DiffSyncSchema } from "./schema";

export type DiffDto<
  T extends DiffSyncBaseSchema,
  TType extends keyof T & string = keyof T & string,
> = TType extends keyof T & string
  ? {
      diffId: string;
      uniqueKeys: string[];
      type: TType;
      local: T[TType] | null;
      remote: T[TType] | null;
      error: string | null;
      isPushable: boolean;
      isPullable: boolean;
    }
  : never;

type SchemaEntry<T extends DiffSyncBaseSchema, TType extends keyof T & string> = [TType, Serializer<T[TType]>];
const schemaEntries = <T extends DiffSyncBaseSchema>(schema: DiffSyncSchema<T>): SchemaEntry<T, keyof T & string>[] =>
  _.entries(schema) as any;

export const diffDtoSerialzier = <T extends DiffSyncBaseSchema>(schema: DiffSyncSchema<T>) =>
  Serializers.first(
    ...schemaEntries(schema).map(([type, serializer]) =>
      Serializers.object<DiffDto<T, keyof T & string>>({
        diffId: Serializers.string,
        uniqueKeys: Serializers.string.array(),
        type: Serializers.stringOneOf(type),
        local: Serializers.nullable(serializer),
        remote: Serializers.nullable(serializer),
        error: Serializers.string.nullable(),
        isPushable: Serializers.boolean,
        isPullable: Serializers.boolean,
      } as any),
    ),
  );

export const diffSyncActions = <T extends DiffSyncBaseSchema>(schema: DiffSyncSchema<T>) => ({
  diffQuery: HttpAction({
    path: "/diffSync/diff",
    method: "get",
    qsSerializer: Serializers.object({
      type: Serializers.stringOneOf<keyof T & string>(...Object.keys(schema)),
      isPushable: Serializers.booleanString,
      isPullable: Serializers.booleanString,
    }).partial(),
    requiresAuth: true as const,
    valueSerializer: diffDtoSerialzier(schema).array(),
  }),

  refreshDiffs: HttpAction({
    path: "/diffSync/refresh",
    method: "post",
    qsSerializer: Serializers.object({
      type: Serializers.stringOneOf<keyof T & string>(...Object.keys(schema)),
    }),
    requiresAuth: true as const,
  }),

  pullDiffs: HttpAction({
    path: "/diffSync/pull",
    method: "post",
    bodySerializer: Serializers.object({
      type: Serializers.stringOneOf<keyof T & string>(...Object.keys(schema)),
      diffIds: Serializers.string.array().optional(),
    }),
    requiresAuth: true as const,
  }),

  pushDiffs: HttpAction({
    path: "/diffSync/push",
    method: "post",
    bodySerializer: Serializers.object({
      type: Serializers.stringOneOf<keyof T & string>(...Object.keys(schema)),
      diffIds: Serializers.string.array().optional(),
    }),
    requiresAuth: true as const,
  }),
});

export type DiffSyncHttpHandlers<T extends DiffSyncBaseSchema> = HandlerMap<
  ReturnType<typeof diffSyncActions<T>>,
  AnyUser
>;
